import StructuredStorage from '@tencent/memoire-structured-storage';
import IDBEngine from '@tencent/memoire-idb-engine';
import { SnapshotMetaItemsModel, SnapshotDataItemsModel } from './models';
import type { SnapshotData } from './definitions';
import type { Collection } from '@tencent/memoire-structured-storage';

const DATABASE_NAME = 'TencentDocs-PPT';

export class SnapshotStorage {
    private globalPadId: string;
    private userId: string;
    private db: StructuredStorage;
    private store: {
        meta: Collection<SnapshotMetaItemsModel>;
        data: Collection<SnapshotDataItemsModel>;
    };

    constructor(globalPadId: string, userId: string) {
        if (!globalPadId || !userId) {
            throw new Error('missing globalPadId or userId');
        }

        this.globalPadId = globalPadId;
        this.userId = userId;

        this.db = new StructuredStorage({
            name: DATABASE_NAME,
            version: 1,
            engine: new IDBEngine(),
        });

        this.db.register(SnapshotMetaItemsModel);
        this.db.register(SnapshotDataItemsModel);

        this.store = {
            meta: this.db.collection(SnapshotMetaItemsModel),
            data: this.db.collection(SnapshotDataItemsModel),
        };
    }

    public async getById(id: string): Promise<SnapshotDataItemsModel> {
        const data = await this.store.data.get(id);
        if (!data) {
            throw new Error(`NotFoundError: snapshot with id=${id} not exists`);
        }
        return data;
    }

    public async save(data: SnapshotData<unknown, unknown>, id?: string) {
        const meta = {
            globalPadId: this.globalPadId,
            userId: this.userId,
            id: id || [this.globalPadId, this.userId, Date.now()].join(':'),
        };

        const serializedData = await data.serialize();
        await this.store.meta.upsert(SnapshotMetaItemsModel.fromSnapshotMeta(meta, serializedData.size));
        await this.store.data.upsert(SnapshotDataItemsModel.fromSerializedData(meta, serializedData));
    }

    public async list(): Promise<SnapshotMetaItemsModel[]> {
        const metaItems = await this.store.meta
            .where({ globalPadId: this.globalPadId, userId: this.userId })
            .orderBy('createdAt')
            .values();
        return metaItems;
    }

    public async remove(...ids: string[]) {
        await this.store.meta.where({ id: { in: ids } }).clear();
        await this.store.data.where({ id: { in: ids } }).clear();
    }

    public statistics(snapshots: SnapshotMetaItemsModel[]): SnapshotStatistics {
        if (!Array.isArray(snapshots)) {
            throw new Error('argument must be an array of snapshot');
        }

        let count = 0;
        let totalSizeInBytes = 0;

        snapshots.forEach((snapshot) => {
            count += 1;
            totalSizeInBytes += snapshot.size || 0;
        });

        return { count, totalSizeInBytes };
    }
}

interface SnapshotStatistics {
    count: number;
    totalSizeInBytes: number;
}
